/**
 * Single French messages catalogue for the whole SPA.
 *
 * Per `docs/CONVENTIONS.md` §1, every user-facing string lives here and is
 * read through {@link t}, never concatenated at the call site. Keys are
 * dot-paths into {@link messages}; interpolation uses `{{name}}` tokens.
 */

import { adminMessages } from '../features/admin/i18n.fr';
import { householdMessages } from '../features/household/i18n.fr';
import { sessionMessages } from '../features/session/i18n.fr';
import { offlineMessages } from '../offline/i18n.fr';

const messages = {
  session: sessionMessages,
  household: householdMessages,
  admin: adminMessages,
  offline: offlineMessages,
  app: {
    name: 'Dring',
  },
  userMenu: {
    open: 'Menu du compte',
    profile: 'Mon profil',
    logout: 'Se déconnecter',
    adminConsole: 'Console admin',
  },
  common: {
    loading: 'Chargement…',
    retry: 'Réessayer',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    cancel: 'Annuler',
    close: 'Fermer',
    error: 'Une erreur est survenue',
    requiredField: 'Ce champ est obligatoire',
    logout: 'Déconnexion',
  },
  auth: {
    login: {
      title: 'Connexion',
      lead: 'Recevez un lien de connexion par e-mail, sans mot de passe.',
      emailLabel: 'Adresse e-mail',
      emailPlaceholder: 'camille@example.org',
      submit: 'Recevoir le lien',
      submitting: 'Envoi en cours…',
      invalidEmail: 'Saisissez une adresse e-mail valide',
      genericError: "L'envoi a échoué, réessayez dans un instant",
    },
    checkEmail: {
      title: 'Vérifiez votre boîte mail',
      body: 'Un lien de connexion valable 15 minutes vient d’être envoyé à {{email}}. Ouvrez-le depuis ce téléphone pour continuer.',
      resend: 'Renvoyer le lien',
      resendCooldown: 'Renvoyer dans {{seconds}} s',
      changeEmail: 'Utiliser une autre adresse',
    },
    verify: {
      verifying: 'Connexion en cours…',
      missingToken: 'Lien de connexion invalide : le jeton est manquant.',
      error: 'Ce lien de connexion est invalide, expiré ou déjà utilisé.',
      backToLogin: 'Retour à la connexion',
    },
    pending: {
      title: 'Compte en attente de validation',
      body: 'Votre profil a bien été enregistré. Un·e administrateur·rice doit valider votre compte avant que vous puissiez accéder à l’application. Vous recevrez un e-mail dès que ce sera fait.',
      refresh: 'Vérifier à nouveau',
      logout: 'Se déconnecter',
    },
  },
  onboarding: {
    title: 'Complétez votre profil',
    lead: 'Ces informations nous permettent de mieux organiser le porte-à-porte.',
    firstName: 'Prénom',
    lastName: 'Nom',
    phone: 'Téléphone',
    rgpd: "J'accepte que ces informations soient utilisées pour organiser le porte-à-porte, conformément au RGPD.",
    submit: 'Valider mon profil',
    errors: {
      firstName: 'Indiquez votre prénom',
      lastName: 'Indiquez votre nom',
      rgpd: "L'accord RGPD est nécessaire pour continuer",
    },
    success: 'Profil enregistré',
  },
  profile: {
    title: 'Mon profil',
    lead: 'Mettez à jour vos coordonnées.',
  },
  home: {
    title: 'Dring — bientôt',
    body: 'Le porte-à-porte arrive bientôt. Votre compte est prêt : revenez ici dès que les prochaines fonctionnalités seront en ligne.',
  },
  pwa: {
    install: {
      title: 'Installer Dring',
      body: 'Ajoutez l’application à votre écran d’accueil pour l’utiliser hors-ligne, en un geste.',
      action: 'Installer',
      dismiss: 'Plus tard',
    },
    update: {
      title: 'Nouvelle version disponible',
      body: 'Une mise à jour de l’application est prête.',
      action: 'Mettre à jour',
      dismiss: 'Plus tard',
    },
    offlineReady: 'Application prête pour une utilisation hors-ligne.',
  },
} as const;

/** Recursively-flattenable message tree, as authored above. */
type Messages = typeof messages;

/** Union of every valid dot-path key into {@link messages}. */
type PathsToValues<T, Prefix extends string = ''> = T extends string
  ? Prefix
  : {
      [K in keyof T & string]: PathsToValues<T[K], Prefix extends '' ? K : `${Prefix}.${K}`>;
    }[keyof T & string];

/** A dot-path key that resolves to a string in the messages catalogue. */
export type MessageKey = PathsToValues<Messages>;

/**
 * Look up a message by its dot-path key, resolving `{{name}}` placeholders.
 *
 * @param key - Dot-path into the messages catalogue, e.g. `"auth.login.title"`.
 * @param vars - Values substituted for `{{name}}` placeholders in the string.
 * @returns The resolved, interpolated message.
 * @throws {Error} If `key` does not resolve to a string leaf.
 */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const value = key.split('.').reduce<unknown>((node, segment) => {
    if (node && typeof node === 'object' && segment in node) {
      return (node as Record<string, unknown>)[segment];
    }
    return undefined;
  }, messages);

  if (typeof value !== 'string') {
    throw new Error(`Missing i18n message for key "${key}"`);
  }

  if (!vars) {
    return value;
  }

  return value.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}
